import { TicketSymbol } from "@lib/titan2-slingo/dist/game/view/abstract/display/TicketSymbol";
import { TitanSprite } from "@lib/titan2/dist/game/control/TitanSprite";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { DestroyUtil } from "@lib/titan2/dist/util/view/DestroyUtil";
import { SpineUtil } from "@lib/titan2/dist/util/view/SpineUtil";
import { gsap } from "gsap";
import { addToTitanSpineSlot } from "../../../utils/SpineUtils";
import { RandomUtil } from "@lib/titan2/dist/util/random/RandomUtil";
import { SpriteProperties } from "@lib/titan2/dist/game/control/properties/SpriteProperties";
import { Point } from "pixi.js";
import { TicketCellData } from "@lib/titan2-slingo/dist/api/model/data/TicketCellData";
/**
 * GameTicketSymbol.ts
 */
export class GameTicketSymbol extends TicketSymbol {
    constructor() {
        super(...arguments);
        this.HIGHLIGHT_PULSE_SCALE = 1.1;
        this.HIGHLIGHT_PULSE_DURATION = 0.9;
    }
    createProperties(properties, portraitProperties) {
        super.createProperties(properties, portraitProperties);
        this.landscapeProperties = properties;
        this.portraitProperties = portraitProperties;
        this.uiProperties = this.uiProperties;
    }
    destroy() {
        gsap.killTweensOf(this);
        this.spine.removeCompleteHandlers();
        this.spine.removeEventHandlers();
        this.spine.removeStartHandlers();
        this.spine.stop();
        super.destroy();
        DestroyUtil.destroy(this.symbolHighlight);
        DestroyUtil.destroy(this.symbolHighlightSuperWild);
        DestroyUtil.destroy(this.background);
        this.symbolHighlight = null;
        this.symbolHighlightSuperWild = null;
        this.background = null;
    }
    create() {
        this.symbolScale = 1;
        super.create();
        this.button.alpha = 0;
    }
    createStates() {
        super.createStates();
    }
    createSymbol() {
        this.data = new TicketCellData(1, 0, 0);
        this.updateSymbol();
        addToTitanSpineSlot(this.spine, this.symbolSprite, "dynamic-text-base");
        this.removeHighlight();
    }
    addEventListeners() {
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
    }
    removeEventListeners() {
        this.unregisterGame(CommonGameEvent.SPIN_REQUEST);
    }
    updateSymbol() {
        this.symbolSprite?.destroy();
        this.symbolHighlightSprite?.destroy();
        this.symbolHighlightWildSprite?.destroy();
        this.symbolHighlightSuperWildSprite?.destroy();
        const number = this.data.getNumber() < 10 ? `0${this.data.getNumber()}` : this.data.getNumber().toString();
        this.symbolSprite = TitanSprite.createSprite(undefined, `number`, new SpriteProperties(`grid${number}`, `NumberAssets`, new Point(0, 0), new Point(1, 1), new Point(0.5, 0.5)));
        this.symbolHighlightSprite = TitanSprite.createSprite(undefined, `number`, new SpriteProperties(`grid${number}`, `NumberAssets`, new Point(0, 0), new Point(1, 1), new Point(0.5, 0.5)));
        this.symbolHighlightWildSprite = TitanSprite.createSprite(undefined, `number`, new SpriteProperties(`gridwild${number}`, `NumberAssets`, new Point(0, 0), new Point(1, 1), new Point(0.5, 0.5)));
        this.symbolHighlightSuperWildSprite = TitanSprite.createSprite(undefined, `number`, new SpriteProperties(`gridsuperwild${number}`, `NumberAssets`, new Point(0, 0), new Point(1, 1), new Point(0.5, 0.5)));
    }
    disable() {
        this.button.setEnabled(false);
        this.enabled = false;
        if (!this.dabbing) {
            this.removeHighlight();
        }
    }
    clearHighlightTextParents() {
        if (!this.symbolSprite || !this.symbolHighlightSprite || !this.symbolHighlightWildSprite || !this.symbolHighlightSuperWildSprite) {
            return;
        }
        this.symbolSprite.parent?.removeChild(this.symbol);
        this.symbolHighlightSprite.parent?.removeChild(this.symbolHighlightSprite);
        this.symbolHighlightWildSprite.parent?.removeChild(this.symbolHighlightWildSprite);
        this.symbolHighlightSuperWildSprite.parent?.removeChild(this.symbolHighlightSuperWildSprite);
    }
    highlight(animate) {
        Logger.verbose("highlught");
        this.clearHighlightTextParents();
        addToTitanSpineSlot(this.spine, this.symbolHighlightSprite, "dynamic-text-base");
        SpineUtil.play(this.spine, "0-highlight-anticipation" /* TicketSymbolAnims.HIGHLIGHT_ANTICIPATION */);
        if (animate) {
            this.pulseStart();
        }
    }
    highlightWild(animate) {
        Logger.verbose("highlughtSuperWild");
        this.clearHighlightTextParents();
        addToTitanSpineSlot(this.spine, this.symbolHighlightWildSprite, "dynamic-text-base");
        SpineUtil.play(this.spine, "0-highlight-wild" /* TicketSymbolAnims.HIGHLIGHT_WILD */);
        if (animate) {
            this.pulseStart();
        }
    }
    highlightSuperWild(animate) {
        Logger.verbose("highlughtSuperWild");
        this.clearHighlightTextParents();
        addToTitanSpineSlot(this.spine, this.symbolHighlightSuperWildSprite, "dynamic-text-base");
        SpineUtil.play(this.spine, "0-highlight-superwild" /* TicketSymbolAnims.HIGHLIGHT_SUPER_WILD */);
        if (animate) {
            this.pulseStart();
        }
    }
    dabSuccess() {
        this.container.removeChild(this.button);
        this.bringToTop();
        this.model.setTicketDabInProgress(this);
        Logger.verbose("dabSuccess");
        SpineUtil.play(this.spine, "0-dab" /* TicketSymbolAnims.DAB */, () => { this.model.setTicketDabComplete(this); });
        this.dabbing = true;
    }
    fullHouse() {
        Logger.verbose("fullhouse");
        SpineUtil.play(this.spine, "0-win" /* TicketSymbolAnims.WIN */, this.handleFullHouseComplete, this);
    }
    bigWin() {
        Logger.verbose("bigWin");
        SpineUtil.play(this.spine, "0-win" /* TicketSymbolAnims.WIN */, this.handleFullHouseComplete, this);
    }
    handleFullHouseComplete() {
        this.reset();
    }
    dab() {
        this.container.removeChild(this.button);
        this.highlight(false);
        this.spine.visible = true;
        this.model.setTicketDabInProgress(this);
        Logger.verbose("dab");
        SpineUtil.play(this.spine, "0-dab" /* TicketSymbolAnims.DAB */, () => { this.model.setTicketDabComplete(this); });
    }
    win() {
        if (this.data.isDabbed()) {
            Logger.verbose("win");
            SpineUtil.play(this.spine, "0-win" /* TicketSymbolAnims.WIN */);
        }
        else {
            this.dabSuccess();
        }
    }
    removeHighlight() {
        this.clearHighlightTextParents();
        addToTitanSpineSlot(this.spine, this.symbolSprite, "dynamic-text-base");
        Logger.verbose("removeHighlight");
        const state = this.data && this.data.isDabbed() ? "0-dabbed" /* TicketSymbolAnims.DABBED */ : "0-idle" /* TicketSymbolAnims.IDLE */;
        const currentTrack = this.spine.getTrackEntry(0);
        if (!currentTrack || !currentTrack.animation || currentTrack.animation.name !== state) { // only play if we're changing anims to prevent the idle loops resetting.
            const maxFrames = this.spine.getState().data.skeletonData.animations.find(animation => animation.name === state).duration * 30;
            SpineUtil.gotoAndPlay(this.spine, RandomUtil.nextInt(0, maxFrames), state, 0, true);
        }
        this.pulseEnd();
    }
    addSymbol(symbol) {
    }
    pulseStart() {
        Logger.verbose("pulseStart");
        this.symbolScale = 1;
        this.symbolTween = gsap.to(this, {
            symbolScale: this.HIGHLIGHT_PULSE_SCALE,
            yoyo: true,
            repeat: -1,
            duration: this.HIGHLIGHT_PULSE_DURATION,
            onUpdate: () => {
                this.symbolSprite.scale.set(this.symbolScale);
                this.symbolHighlightSprite.scale.set(this.symbolScale);
                this.symbolHighlightWildSprite.scale.set(this.symbolScale);
                this.symbolHighlightSuperWildSprite.scale.set(this.symbolScale);
            }
        });
    }
    pulseEnd() {
        Logger.verbose("pulseEnd");
        const progress = this.symbolTween?.progress() || 1;
        const duration = this.HIGHLIGHT_PULSE_DURATION * progress;
        this.symbolTween?.kill();
        if (this.symbolScale !== 1) {
            this.symbolTween = gsap.to(this, {
                symbolScale: 1,
                duration: duration,
                onUpdate: () => {
                    this.symbolSprite.scale.set(this.symbolScale);
                    this.symbolHighlightSprite.scale.set(this.symbolScale);
                    this.symbolHighlightWildSprite.scale.set(this.symbolScale);
                    this.symbolHighlightSuperWildSprite.scale.set(this.symbolScale);
                }
            });
        }
        else {
            this.symbolTween = null;
        }
    }
    handleSpinRequest() {
        if (this) {
            gsap.killTweensOf(this);
            this.pulseEnd();
        }
    }
    showWin() {
        if (!this.data.isDabbed()) {
            SpineUtil.play(this.spine, "0-YCW" /* TicketSymbolAnims.HIGHLIGHT_YCW */);
        }
    }
    hideWin() {
        if (!this.data.isDabbed()) {
            this.removeHighlight();
        }
    }
}
//# sourceMappingURL=GameTicketSymbol.js.map